import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { useNavigate } from "react-router-dom";

const WorkSubTypeMaster = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // ✅ get localStorage stored data
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    estimationTypeName: "",
    workTypeName: "",
    subWorkTypeCode: "",
    subWorkTypeName: "",
    uom: "",
    remarks: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [estimationTypes, setEstimationTypes] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [uomList, setUomList] = useState([]);

  const [ddlLoading, setDdlLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [records, setRecords] = useState([]);

  const recordsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  // ---------- helpers ----------
  const resetFormForNew = async () => {
    setIsEdit(false);
    setEditId(null);
    setErrors({});
    setWorkTypes([]);

    setFormData({
      estimationTypeName: "",
      workTypeName: "",
      subWorkTypeCode: "",
      subWorkTypeName: "",
      uom: "",
      remarks: "",
      isActive: true,
    });

    await fetchNextSubWorkTypeCode();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // ---------- DDLs ----------
  const fetchEstimationDDL = async () => {
    try {
      setDdlLoading(true);
      const res = await axios.get(`${BASE_URL}/api/wst_estimationTypeDDL`, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setEstimationTypes(res.data.list || []);
      } else {
        toast.error(res.data?.message || "Failed to load estimation types");
      }
    } catch (err) {
      console.error("Estimation DDL Error:", err);
      toast.error("Failed to load estimation types");
    } finally {
      setDdlLoading(false);
    }
  };

  const fetchWorkTypeDDL = async (estimationTypeId) => {
    if (!estimationTypeId) {
      setWorkTypes([]);
      return [];
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/wst_workTypeDDL`, {
        params: { estimationTypeId },
        headers: commonHeaders,
      });

      const list = res.data?.list || [];
      setWorkTypes(list);
      return list;
    } catch (err) {
      console.error("WorkType DDL error:", err);
      setWorkTypes([]);
      return [];
    }
  };

  const fetchUomDDL = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/wst_uomDDL`, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setUomList(res.data.list || []);
      } else {
        toast.error(res.data?.message || "Failed to load UOM");
      }
    } catch (err) {
      console.error("UOM fetch error:", err);
      toast.error("Failed to load UOM");
    }
  };

  // ---------- Auto Code ----------
  const fetchNextSubWorkTypeCode = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/wst_getNextSubWorkTypeCode`,
        {
          headers: commonHeaders,
        },
      );

      const nextCode = res.data?.subWorkTypeCode || "WSB1";
      setFormData((prev) => ({ ...prev, subWorkTypeCode: nextCode }));
    } catch (err) {
      console.error("Sub Work Type Code Error:", err);
      toast.error("Failed to get sub work type code");
      setFormData((prev) => ({
        ...prev,
        subWorkTypeCode: prev.subWorkTypeCode || "WSB1",
      }));
    }
  };

  // ---------- List (Server Pagination) ----------
  // ---------- List (Server Pagination) ----------
  const fetchWorkSubTypeList = async (page = 1, searchOverride) => {
    try {
      setLoading(true);

      const search = searchOverride !== undefined ? searchOverride : searchTerm;

      const res = await axios.get(`${BASE_URL}/api/wst_workSubTypeList`, {
        params: { page, limit: recordsPerPage, search },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load list");
        return;
      }

      const list = res.data.list || [];
      const mapped = list.map((item, index) => ({
        id: item.fld_workinvolveditemid,
        sNo: (page - 1) * recordsPerPage + (index + 1),
        estimationTypeName: item.fld_estimationtypename || "",
        workTypeName: item.fld_categoryname || "",
        itemCode: item.fld_itemcode || "",
        estimationProjectWorkSubTypeName: item.fld_description || "",
        uom: item.fld_uomname || "",
        remarks: item.fld_remarks || "",
        selected: false,
      }));

      setRecords(mapped);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error("List fetch error:", err);
      toast.error("Failed to load work sub type list");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (
      !page ||
      page === "..." ||
      page < 1 ||
      page > totalPages ||
      page === currentPage
    )
      return;
    fetchWorkSubTypeList(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  // ---------- Edit ----------
  const handleEditById = async (workInvolvedItemId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/wst_getWorkSubTypeById`, {
        params: { workInvolvedItemId },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to fetch record");
        return;
      }

      const d = res.data.data;

      const estimationId =
        d.fld_estimationtypeid ??
        d.fld_EstimationTypeid ??
        d.fld_EstimationTypeId;
      const workTypeId =
        d.fld_worktypeid ?? d.fld_WorkTypeid ?? d.fld_WorkTypeId;

      const estimationIdStr = estimationId ? String(estimationId) : "";
      const workTypeIdStr = workTypeId ? String(workTypeId) : "";

      setFormData((prev) => ({
        ...prev,
        estimationTypeName: estimationId,
        workTypeName: "",
        subWorkTypeCode: d.fld_itemcode || "",
        subWorkTypeName: d.fld_description || "",
        uom: d.fld_uomid ? String(d.fld_uomid) : "",
        remarks: d.fld_remarks || "",
        isActive: d.fld_isactive === 1 || d.fld_isactive === true,
      }));

      setIsEdit(true);
      setEditId(workInvolvedItemId);
      setShowFilters(true);

      await fetchWorkTypeDDL(estimationId);

      setFormData((prev) => ({
        ...prev,
        workTypeName: workTypeId,
      }));
    } catch (err) {
      console.error("Edit fetch error:", err);
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  // ---------- Delete ----------
  const selectedCount = records.filter((r) => r.selected).length;
  const canDelete = Boolean(editId) || selectedCount >= 1;
  const handleDeleteSingle = async () => {
    // Prefer the record being edited; otherwise fall back to a
    // single checkbox-selected record from the table.
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    let targetId = null;

    if (isEdit && editId) {
      targetId = editId;
    } else if (selectedIds.length === 1) {
      targetId = selectedIds[0];
    } else if (selectedIds.length > 1) {
      toast.info(
        "Please select only one record to delete, or use Delete Selected",
      );
      return;
    }

    if (!targetId) {
      toast.info("Please click Work Sub Type Code to select a record");
      return;
    }

    const ok = window.confirm(
      "Are you sure you want to delete this Work Sub Type?",
    );
    if (!ok) return;

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/wst_deleteWorkSubType`,
        { workInvolvedItemId: targetId },
        { headers: commonHeaders },
      );

      if (res.data?.success) {
        toast.success(res.data.message || "Deleted successfully");
        await fetchWorkSubTypeList(currentPage);

        // Uncheck the row we just deleted, if it was selected via checkbox
        setRecords((prev) =>
          prev.map((r) => (r.id === targetId ? { ...r, selected: false } : r)),
        );

        await resetFormForNew();
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Server error while deleting");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Search ----------
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    fetchWorkSubTypeList(1, searchTerm);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    fetchWorkSubTypeList(1, "");
  };

  const handleSelectRecord = (id, checked) => {
    setRecords((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: checked } : r)),
    );
  };

  const handleDeleteSelected = async () => {
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    if (selectedIds.length === 0) {
      toast.info("Please select at least one record");
      return;
    }

    const ok = window.confirm(
      `Delete ${selectedIds.length} selected record(s)?`,
    );
    if (!ok) return;

    try {
      setSaving(true);

      for (const id of selectedIds) {
        await axios.post(
          `${BASE_URL}/api/wst_deleteWorkSubType`,
          { workInvolvedItemId: id },
          { headers: commonHeaders },
        );
      }

      toast.success("Selected records deleted successfully");
      await fetchWorkSubTypeList(currentPage);

      if (editId && selectedIds.includes(editId)) {
        await resetFormForNew();
      }
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error("Error deleting selected records");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Validation ----------
  const validateForm = () => {
    const newErrors = {};

    if (!formData.estimationTypeName) {
      newErrors.estimationTypeName = "Estimation Type Name is required";
    }
    if (!formData.workTypeName) {
      newErrors.workTypeName = "Work Type Name is required";
    }
    if (!String(formData.subWorkTypeCode || "").trim()) {
      newErrors.subWorkTypeCode = "Sub Work Type Code is required";
    }
    if (!String(formData.subWorkTypeName || "").trim()) {
      newErrors.subWorkTypeName = "Sub Work Type Name is required";
    }
    if (!formData.uom) {
      newErrors.uom = "UOM is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------- Submit (Add / Update) ----------
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Required fields missing");
      return;
    }

    const createdBy = Number(userId);
    if (!createdBy) {
      toast.error("UserId missing! Please login again.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        estimationTypeId: Number(formData.estimationTypeName),
        workTypeId: Number(formData.workTypeName),
        itemCode: String(formData.subWorkTypeCode || "").trim(),
        description: String(formData.subWorkTypeName || "").trim(),
        uomId: Number(formData.uom),
        remarks: formData.remarks || "",
        isActive: formData.isActive,
        createdBy,
      };

      // UPDATE
      if (isEdit && editId) {
        const res = await axios.post(
          `${BASE_URL}/api/wst_updateWorkSubType`,
          {
            ...payload,
            workInvolvedItemId: editId,
          },
          { headers: commonHeaders },
        );

        if (res.data?.success) {
          toast.success(res.data?.message || "Updated successfully");
          await fetchWorkSubTypeList(1);
          await resetFormForNew();
          return;
        } else {
          toast.error(res.data?.message || "Update failed");
          return;
        }
      }

      // ADD
      const res = await axios.post(
        `${BASE_URL}/api/wst_addWorkSubType`,
        payload,
        { headers: commonHeaders },
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved successfully");
        await fetchWorkSubTypeList(1);
        await resetFormForNew();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = async () => {
    toast.info("Form cleared for new entry");
    await resetFormForNew();
  };

  // ---------- Initial load ----------
  useEffect(() => {
    fetchEstimationDDL();
    fetchUomDDL();
    fetchNextSubWorkTypeCode();
    fetchWorkSubTypeList(1);
  }, [commonHeaders]);

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">WORK SUB TYPE MASTER</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white text-black`
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Work Sub Type Master
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search code, description, remarks..."
                  className={`px-3 py-2 rounded-lg border text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
                <button
                  onClick={handleSearchSubmit}
                  className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white text-sm`}
                >
                  Search
                </button>
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className={`px-4 py-2 rounded-lg font-semibold border transition-all duration-200 text-sm ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Entry Form" : "Show Entry Form"}
              </button>
            </div>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showFilters && (
              <div
                className={`p-6 border-t ${
                  isDark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="max-w-2xl mx-auto space-y-4">
                  {/* Estimation Type */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label
                        className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Estimation Type Name{" "}
                        <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={String(formData.estimationTypeName || "")}
                        disabled={isEdit || ddlLoading}
                        onChange={(e) => {
                          const estimationTypeId = e.target.value;
                          handleInputChange(
                            "estimationTypeName",
                            estimationTypeId,
                          );
                          handleInputChange("workTypeName", "");
                          setWorkTypes([]);
                          fetchWorkTypeDDL(estimationTypeId);
                        }}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.estimationTypeName ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        } ${isEdit || ddlLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                      >
                        <option value="">Select</option>
                        {estimationTypes.map((et) => (
                          <option
                            key={et.fld_EstimationTypeid || et.estimationTypeId}
                            value={String(
                              et.fld_EstimationTypeid || et.estimationTypeId,
                            )}
                          >
                            {et.fld_EstimationTypeName || et.estimationTypeName}
                          </option>
                        ))}
                      </select>
                      {errors.estimationTypeName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.estimationTypeName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Work Type */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label
                        className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Work Type Name <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={String(formData.workTypeName || "")}
                        disabled={!formData.estimationTypeName || isEdit}
                        onChange={(e) =>
                          handleInputChange("workTypeName", e.target.value)
                        }
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.workTypeName ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        } ${!formData.estimationTypeName || isEdit ? "opacity-70 cursor-not-allowed" : ""}`}
                      >
                        <option value="">Select</option>
                        {workTypes.map((wt) => (
                          <option
                            key={wt.fld_Categoryid || wt.workTypeId}
                            value={String(wt.fld_Categoryid || wt.workTypeId)}
                          >
                            {wt.fld_Categoryname || wt.workTypeName}
                          </option>
                        ))}
                      </select>
                      {errors.workTypeName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.workTypeName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Code */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label
                        className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Sub Work Type Code{" "}
                        <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.subWorkTypeCode}
                        disabled
                        className={`w-full px-4 py-2 border rounded-lg opacity-80 cursor-not-allowed ${
                          errors.subWorkTypeCode ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-gray-100 border-gray-300 text-gray-900"
                        }`}
                      />
                      {errors.subWorkTypeCode && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.subWorkTypeCode}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-4">
                      <label
                        className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Sub Work Type Name{" "}
                        <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <textarea
                        value={formData.subWorkTypeName}
                        onChange={(e) =>
                          handleInputChange("subWorkTypeName", e.target.value)
                        }
                        rows={4}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          errors.subWorkTypeName ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                      {errors.subWorkTypeName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.subWorkTypeName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* UOM */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label
                        className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        UOM <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={String(formData.uom || "")}
                        onChange={(e) =>
                          handleInputChange("uom", e.target.value)
                        }
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.uom ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">Select</option>
                        {uomList.map((u) => (
                          <option
                            key={u.fld_uomid || u.uomId}
                            value={String(u.fld_uomid || u.uomId)}
                          >
                            {u.fld_uomname || u.uomName}
                          </option>
                        ))}
                      </select>
                      {errors.uom && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.uom}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-4">
                      <label
                        className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Remarks
                      </label>
                    </div>
                    <div className="col-span-8">
                      <textarea
                        value={formData.remarks}
                        onChange={(e) =>
                          handleInputChange("remarks", e.target.value)
                        }
                        rows={3}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Active */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4" />
                    <div className="col-span-8">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!formData.isActive}
                          onChange={(e) =>
                            handleInputChange("isActive", e.target.checked)
                          }
                          className="mr-2"
                        />
                        <span
                          className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Is Active
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-center gap-4 pt-4 flex-wrap">
                    <button
                      onClick={handleSubmit}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        saving ? "opacity-70 cursor-not-allowed" : ""
                      } ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaCheck />
                      {saving ? "Saving..." : "Submit"}
                    </button>

                    <button
                      onClick={handleAddNew}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaPlus />
                      Add New
                    </button>

                    <button
                      onClick={handleDeleteSingle}
                      disabled={!canDelete}
                      className={`relative z-10 flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${
                        !canDelete
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:shadow-xl transform hover:-translate-y-0.5"
                      } transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                    >
                      🗑 Delete
                    </button>

                    <button
                      onClick={handleDeleteSelected}
                      disabled={records.every((r) => !r.selected)}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${
                        records.every((r) => !r.selected)
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:shadow-xl transform hover:-translate-y-0.5"
                      } transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                    >
                      🗑 Delete Selected
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Item Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Estimation Project Work SubType Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      UOM
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Select
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="5"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    records.map((record, index) => (
                      <tr
                        key={record.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.sNo}
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 font-medium ${
                            isDark ? "text-blue-300" : "text-blue-600"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => handleEditById(record.id)}
                            className="underline hover:opacity-80"
                            title="Click to edit"
                          >
                            {record.itemCode}
                          </button>
                        </td>

                        <td
                          className={`px-4 py-3 text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          <div className="max-w-md">
                            <p className="text-justify leading-relaxed">
                              {record.estimationProjectWorkSubTypeName}
                            </p>
                          </div>
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.uom}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!record.selected}
                            onChange={(e) =>
                              handleSelectRecord(record.id, e.target.checked)
                            }
                            className={`w-4 h-4 cursor-pointer ${
                              isDark ? "accent-blue-400" : "accent-blue-600"
                            }`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {/* Pagination */}
          <div className="flex justify-start mt-6">
            <div className="flex gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === 1
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              {renderPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(page)}
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : page === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                          ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === totalPages || totalPages === 0
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default WorkSubTypeMaster;
